import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import NoteLinkedFilePreview from 'notebook/notes/attachments/NoteLinkedFilePreview';
import GridItemWrapper from 'notebook/notes/groups/note-part-grid/GridItemWrapper';
import {
  GRID_CONTAINED_IMAGE_PADDING,
  MAX_ITEM_WIDTH,
  MIN_ITEM_WIDTH,
} from 'notebook/notes/groups/note-part-grid/NotePartGridGroupConstants';
import {DraggableFlowItem} from 'notebook/notes/groups/note-part-grid/util/computeRowsForFlowLayout';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {FromServerNoteLinkedFile, NoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import {BLUE_LIGHT, GRAY} from 'ui-library/Colors';
import {DnDDragHandleIcon, DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import {CenteredSpinner} from 'ui-library/Spinner';
import {ElementClipboardEvent, ElementMouseEvent} from 'util/Types';

import {GridMovementDirection} from './KeyboardNavigationConstants';

export interface DraggableItemProps {
  item: DraggableFlowItem;
  dragHandleProps: DnDDragHandleProps;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  isSelectedInGrid: boolean;
  onRemoveItem: (index: number) => void;
  onFinalizeItemWidth: (index: number, width: number) => void;
  onSelectInGrid: (index: number) => void;
  onMoveSelectionInGrid: (index: number, direction: GridMovementDirection) => void;
  onSetClipboard: (e: ElementClipboardEvent) => void;
  onPaste?: (e: ElementClipboardEvent) => void;
  requestSelection?: (args: {selection: BenchlingSelection}) => void;
  isLockedWithEditWarn: () => boolean;
  onUpdateNoteLinkedObject: (index: number, updates: Partial<NoteLinkedFile>) => void;
  onSetUpdatedNoteLinkedFile: (notePartCid: string, noteLinkedFile: FromServerNoteLinkedFile) => void;
  onSetImageFromS3Location: (
    index: number,
    cid: string,
    previewS3Location: string,
    itemWidth: number,
    name: string,
    edits?: string
  ) => Promise<void>;
}

function DraggableItem({
  item,
  dragHandleProps,
  editor,
  editorProps,
  isSelectedInGrid,
  onRemoveItem,
  onFinalizeItemWidth,
  onSelectInGrid,
  onMoveSelectionInGrid,
  onSetClipboard,
  onPaste,
  requestSelection,
  isLockedWithEditWarn,
  onUpdateNoteLinkedObject,
  onSetUpdatedNoteLinkedFile,
  onSetImageFromS3Location,
}: DraggableItemProps): JSX.Element {
  const {notePart, index} = item;
  const nlf = notePart.item as NoteLinkedFile | null;
  const resolvedId = nlf?.cid ? editorProps.getIdFromCid(nlf.cid) : undefined;
  const itemId = resolvedId != null ? String(resolvedId) : (notePart.item_id ?? null);
  const gridItemWidth = Math.max(notePart.itemWidth ?? MAX_ITEM_WIDTH, MIN_ITEM_WIDTH);

  // Select the item immediately on mousedown and stop propagation so the
  // mediocre editor doesn't try to handle drag-handle events. Selecting here
  // (rather than relying on handleClick) is necessary because DnDBlock applies
  // pointer-events:none on START_DRAGGING, which prevents click from reaching
  // the DraggableItem — see the onMouseDown comment in NotePartGrid's container.
  const handleDragMouseDown = useCallback(
    (e: ElementMouseEvent) => {
      onSelectInGrid(index);
      e.stopPropagation();
      dragHandleProps.onMouseDown(e);
    },
    [dragHandleProps, index, onSelectInGrid]
  );

  const handlePlaceholderMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      onSelectInGrid(index);
    },
    [index, onSelectInGrid]
  );

  const handleMoveSelectionInGrid = useCallback(
    (direction: GridMovementDirection) => {
      onMoveSelectionInGrid(index, direction);
    },
    [index, onMoveSelectionInGrid]
  );

  // TFI callbacks for checkout/sync
  const handleUpdateNoteLinkedObject = useCallback(
    (updates: Partial<NoteLinkedFile>) => {
      onUpdateNoteLinkedObject(index, updates);
    },
    [index, onUpdateNoteLinkedObject]
  );

  const handleSetUpdatedNoteLinkedFile = useCallback(
    (noteLinkedFile: FromServerNoteLinkedFile) => {
      onSetUpdatedNoteLinkedFile(notePart.cid, noteLinkedFile);
    },
    [notePart.cid, onSetUpdatedNoteLinkedFile]
  );

  const handleSetImageFromS3Location = useCallback(
    (cid: string, previewS3Location: string, itemWidth: number, name: string, edits?: string) =>
      onSetImageFromS3Location(index, cid, previewS3Location, itemWidth, name, edits),
    [index, onSetImageFromS3Location]
  );

  const handleFinalizeItemWidth = useCallback(
    (width: number) => {
      onFinalizeItemWidth(index, width);
    },
    [index, onFinalizeItemWidth]
  );

  const handleRemoveItem = useCallback(() => {
    onRemoveItem(index);
  }, [index, onRemoveItem]);

  // Show an uploading spinner for placeholder note parts that don't have an item yet.
  // We check `placeholder && !item` rather than `task` because the TrayTask system
  // (addTasksToNote/removeTasksFromNote) only hydrates `task` on top-level note.noteParts,
  // not on containedNoteParts within a NoteLinkedGroup. A future improvement could wire up
  // addTasksToNote to recurse into NotePartGrid groups, which would enable richer
  // task-based progress display (e.g., upload percentage).
  if (notePart.placeholder && !notePart.item) {
    return (
      <DraggableItemPlaceholder
        width={gridItemWidth}
        onClick={handleClick}
        onMouseDown={handlePlaceholderMouseDown}
      />
    );
  }

  return (
    <GridItemWrapper
      cid={notePart.cid}
      initialItemWidth={gridItemWidth}
      isSelectedInGrid={isSelectedInGrid}
      isLockedWithEditWarn={isLockedWithEditWarn}
      storeState={editorProps.storeState}
      restoreState={editorProps.restoreState}
      requestSelection={requestSelection}
      onDetach={handleRemoveItem}
      onMove={handleMoveSelectionInGrid}
      onSetClipboard={onSetClipboard}
      onPaste={onPaste}
    >
      {({setItemWidth, itemWidth: wrapperItemWidth = gridItemWidth}) => (
        // notePartGrid-itemContent class is used by PreviewResizer to find the resize parent DOM node similar
        // to .mediocre-captionEditable-component.
        <div
          className={`notePartGrid-itemContent ${css(styles.itemWrapper, isSelectedInGrid && styles.itemWrapperSelected)}`}
          style={{width: wrapperItemWidth}}
          {...getTestProps({component: 'DraggableItem', element: 'itemContent'})}
          draggable={false}
          onClick={handleClick}
        >
          {!editorProps.locked && (
            <div
              className={`${css(styles.dragHeader)} dnd-drag-handle`}
              onMouseDown={handleDragMouseDown}
              aria-label="Drag to move"
            >
              {/* noop because the parent div's handleDragMouseDown handles the mousedown; */}
              {/* passing dragHandleProps.onMouseDown here would double-fire the drag start. */}
              <DnDDragHandleIcon className={dragHandleProps.className} onMouseDown={noop} />
            </div>
          )}
          <NoteLinkedFilePreview
            fileId={editorProps.fileId}
            locked={editorProps.locked}
            item={notePart.item!}
            itemId={itemId}
            placeholder={notePart.placeholder ?? false}
            task={notePart.task}
            editorWidth={MAX_ITEM_WIDTH}
            alignment="left"
            isSelected={isSelectedInGrid}
            permissions={editorProps.permissions}
            enableUnlockFile={editor.props.cidIdMap != null}
            insertExcerptsHandler={noop}
            updateNoteLinkedObject={handleUpdateNoteLinkedObject}
            notePartCid={notePart.cid}
            setImageFromS3Location={handleSetImageFromS3Location}
            setNoteLinkedFileFromS3Location={editor.setNoteLinkedFileFromS3Location}
            setItemWidth={setItemWidth}
            itemWidth={wrapperItemWidth}
            imagePadding={GRID_CONTAINED_IMAGE_PADDING}
            setUpdatedNoteLinkedFile={handleSetUpdatedNoteLinkedFile}
            handleDropBenchlingFile={noop}
            handleDropExternalFiles={noop}
            uploadFile={noop}
            finalizeItemWidth={handleFinalizeItemWidth}
          />
        </div>
      )}
    </GridItemWrapper>
  );
}

function DraggableItemPlaceholder({
  width,
  onClick,
  onMouseDown,
}: {
  width: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
}): JSX.Element {
  return (
    <div
      className={css(styles.itemWrapper, styles.placeholderItemWrapper)}
      style={{width}}
      draggable={false}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      <CenteredSpinner />
      <div className="u-text-uppercase u-small-spacing-top text-center">Uploading...</div>
    </div>
  );
}

const styles = StyleSheet.create({
  itemWrapper: {
    backgroundColor: Color.WHITE,
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    // border-box so that `width` includes padding + border, matching what DnDBlock's fitContent sees
    boxSizing: 'border-box' as const,
    minWidth: `${MIN_ITEM_WIDTH}px`,
    padding: '4px',
    paddingTop: '28px',
    position: 'relative',
    transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
    ':hover': {
      backgroundColor: Color.GRAY_50,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    ':hover .dnd-drag-handle': {
      opacity: 1,
    },
  },
  placeholderItemWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
  },
  itemWrapperSelected: {
    boxShadow: `inset 0 0 0 2px ${BLUE_LIGHT}`,
    ':hover': {
      backgroundColor: Color.GRAY_50,
      boxShadow: `inset 0 0 0 2px ${BLUE_LIGHT}, 0 2px 8px rgba(0, 0, 0, 0.15)`,
    },
  },
  dragHeader: {
    // Full-width header area that serves as the drag handle
    alignItems: 'center',
    color: Color.GRAY_800,
    cursor: 'grab',
    display: 'flex',
    fontSize: '16px',
    height: '24px',
    justifyContent: 'flex-start',
    left: 0,
    opacity: 0,
    paddingLeft: '4px',
    position: 'absolute',
    right: 0,
    top: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 10,
  },
});

export default DraggableItem;
